import { type CSSProperties, memo, type ReactNode } from 'react';

import { ContextMenu, type ContextMenuOption, renderContextMenuItems } from '@air/primitive-context-menu';
import { cn } from '@air/utils-tailwind';

import { useAssetModalLeftPanel } from '~/components/AssetModalV2/AssetModalLeftPanelContextProvider';
import { useAssetModalRightPanel } from '~/components/AssetModalV2/AssetModalRightPanelContextProvider';
import { useAssetModalVersionsPanel } from '~/components/AssetModalV2/AssetModalVersionsPanelContextProvider';
import { useAssetModalEscKeyHandler } from '~/components/AssetModalV2/hooks/useAssetModalEscKeyHandler';
import { useKeyboardShortcutsModal } from '~/components/AssetModalV2/hooks/useKeyboardShortcutsModal';
import { useAssetModalStore } from '~/components/v2/components/asset-modal/AssetModalStoreContext';
import { Panel } from '~/components/v2/components/shared/panel/Panel';
import { PanelContent } from '~/components/v2/components/shared/panel/PanelContent';
import { AssetModalPanelState } from '~/components/v2/store/features/asset-modal/slices/createAssetModalPanelSlice';

export type AssetModalDesktopLayoutProps = {
  content?: ReactNode;
  contextMenuOptions?: ContextMenuOption[];
  header?: ReactNode;
  /**
   * Right inset for the modal content row when another surface shares the screen (today the
   * workspace agent panel). A px number or CSS length (for example `calc(var(...) + 4px)` on ui2026).
   * The header keeps the full viewport width. `0` leaves geometry unchanged.
   */
  insetRight?: CSSProperties['marginRight'];
  /** When true, the content row's right inset updates without a 150ms margin transition (live drag). */
  disableInsetMarginTransition?: boolean;
  onClose: () => void;
  renderBottomPanel?: ({ open }: { open: boolean }) => ReactNode;
  renderVersionsPanel?: ({ open }: { open: boolean }) => ReactNode;
  renderLeftPanel?: ({ open }: { open: boolean }) => ReactNode;
  renderRightPanel?: ({ open }: { open: boolean }) => ReactNode;
};

export const AssetModalDesktopLayout = memo(
  ({
    content,
    contextMenuOptions,
    header,
    insetRight = 0,
    disableInsetMarginTransition = false,
    onClose,
    renderBottomPanel,
    renderVersionsPanel,
    renderLeftPanel,
    renderRightPanel,
  }: AssetModalDesktopLayoutProps) => {
    const { isVersionsPanelOpen } = useAssetModalVersionsPanel();
    const { isLeftPanelOpen } = useAssetModalLeftPanel();
    const { isRightPanelOpen } = useAssetModalRightPanel();
    const bottomPanel = useAssetModalStore((store) => store.bottomPanel);
    const theme = useAssetModalStore((store) => store.theme);

    useKeyboardShortcutsModal();
    useAssetModalEscKeyHandler({ onClose });

    return (
      <div className="fixed top-0 left-0 flex size-full flex-col overflow-hidden ads:bg-ads-gray-4 bg-gray-4">
        <header className="flex shrink-0 items-center ads:text-metadata text-gray-10" data-testid="ASSET_MODAL_HEADER">
          {header}
        </header>

        <div
          className={cn(
            'flex min-h-0 grow flex-col gap-2 px-2 ui2026:px-1 pt-0 pb-2 ui2026:pb-1',
            !disableInsetMarginTransition && 'motion-safe:transition-[margin-right] motion-safe:duration-150',
          )}
          // Held back for the agent panel. On this row rather than the root so the header above
          // keeps the full viewport width, with the panel tucked beneath it — see the
          // interim-geometry note in `useWorkspaceAgentPanelViewport` before moving it back up.
          // Margin, not padding: an inline `paddingRight` would clobber horizontal padding above;
          // that right padding is the gap between this content and the panel.
          // `duration-150` matches the agent's `animate-width-in` / `animate-width-out` on open/close.
          // Suppress only during docked drag so live `calc(var(...))` inset tracks the pointer.
          style={{ marginRight: insetRight }}
        >
          {/* ui2026: one island — sidebars flush beside the viewer, video editor flush below,
              square interior corners, 1px gray-4 seams. Outer 12px radius lives here.
              Pre-ui2026 keeps gapped rounded cards. The 4px gap to the agent panel is
              `ui2026:px-1` on the parent, not this island. */}
          <div className="flex min-h-0 grow flex-col gap-2 ui2026:gap-0 ui2026:divide-y ads:divide-default ui2026:divide-gray-4 ui2026:overflow-hidden ui2026:rounded-12">
            <div className="flex min-h-0 grow gap-2 ui2026:gap-0 ui2026:divide-x ads:divide-default ui2026:divide-gray-4">
              <Panel open={isVersionsPanelOpen}>
                <PanelContent className="animation-duration-150 animation-width-[305px] h-full w-[305px] shrink-0 overflow-y-auto rounded-12 ui2026:rounded-none ads:bg-base bg-gray-1 data-[state=closed]:animate-width-out data-[state=open]:animate-width-in">
                  {renderVersionsPanel?.({
                    open: isVersionsPanelOpen,
                  })}
                </PanelContent>
              </Panel>

              <Panel open={isLeftPanelOpen}>
                <PanelContent className="animation-duration-150 animation-width-[348px] h-full w-[348px] shrink-0 overflow-y-auto rounded-12 ui2026:rounded-none ads:bg-base bg-gray-1 data-[state=closed]:animate-width-out data-[state=open]:animate-width-in">
                  {renderLeftPanel?.({
                    open: isLeftPanelOpen,
                  })}
                </PanelContent>
              </Panel>

              <ContextMenu
                data-theme={theme}
                trigger={
                  <div className="flex-1 overflow-hidden rounded-12 ui2026:rounded-none ads:bg-base bg-gray-1">
                    {content}
                  </div>
                }
              >
                {renderContextMenuItems({ options: contextMenuOptions })}
              </ContextMenu>

              <Panel open={isRightPanelOpen}>
                <PanelContent className="animation-duration-150 animation-width-[350px] h-full w-[350px] shrink-0 overflow-hidden overflow-y-auto rounded-12 ui2026:rounded-none ads:bg-base bg-gray-1 data-[state=closed]:animate-width-out data-[state=open]:animate-width-in">
                  {renderRightPanel?.({
                    open: isRightPanelOpen,
                  })}
                </PanelContent>
              </Panel>
            </div>

            <Panel open={bottomPanel === AssetModalPanelState.OPEN}>
              {/* 208px = video editor panel height: 48px zoom toolbar + 160px timeline */}
              <PanelContent className="animation-duration-150 animation-height-[208px] flex h-[208px] w-full shrink-0 overflow-hidden rounded-12 ui2026:rounded-none ads:bg-base bg-gray-1 data-[state=closed]:animate-height-out data-[state=open]:animate-height-in">
                {renderBottomPanel?.({
                  open: bottomPanel === AssetModalPanelState.OPEN,
                })}
              </PanelContent>
            </Panel>
          </div>
        </div>
      </div>
    );
  },
);

AssetModalDesktopLayout.displayName = 'AssetModalDesktopLayout';
